import { Link, NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity,
  ArrowLeft,
  Building2,
  Globe,
  History,
  Inbox,
  Link2,
  LogOut,
  Mail,
  Receipt,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import StatCard from '../components/StatCard';
import Logo from '../components/Logo';
import AppFooter from '../components/AppFooter';
import { formatNumber } from '../lib/format';
import UsersPage from './admin/UsersPage';
import DomainsPage from './admin/DomainsPage';
import ActivityPage from './admin/ActivityPage';
import SalesPage from './admin/SalesPage';
import RawIpnLogsPage from './admin/RawIpnLogsPage';
import MappingsPage from './admin/MappingsPage';
import ResellersPage from './admin/ResellersPage';
import SubdomainsPage from './admin/SubdomainsPage';
import EmailTemplatesPage from './admin/EmailTemplatesPage';

// Admin console layout. Header (logo + sign-out) and stats cards are
// persistent across sub-pages; only the section below the tab bar swaps.
//
// Two tiers of caller reach this page:
//   - super-admin (role === 'admin'): every tab + full stats
//   - reseller-owner (has a Reseller record, but role === 'user'):
//     only Users + Subdomains tabs, no stats. Backend scope-checks every
//     mutation so visibility-only-via-frontend isn't load-bearing.
//
// The scope probe at /api/admin/scope is what distinguishes the two. A 403
// from it means "neither" and renders the access-denied card.
export default function Admin() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const scopeQuery = useQuery({
    queryKey: ['admin-scope'],
    queryFn: () => api.get('/api/admin/scope').then((r) => r.data.scope),
    retry: false,
  });

  const scope = scopeQuery.data;
  const isSuperAdmin = scope?.kind === 'admin';
  const isReseller = scope?.kind === 'reseller';

  const statsQuery = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api.get('/api/admin/stats').then((r) => r.data),
    enabled: isSuperAdmin,
  });

  if (scopeQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-ink-400">
        Loading…
      </div>
    );
  }

  // Reseller v2 §G — resellers no longer share /admin; route them to their
  // own console. /admin is super-admin only.
  if (isReseller) {
    return <Navigate to="/reseller" replace />;
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <Shield className="w-7 h-7" />
          </div>
          <h2 className="text-lg font-semibold">Admin access required</h2>
          <p className="text-sm text-gray-600 mt-2">
            Your account ({user?.email}) is not an admin. Ask an existing admin to
            promote you, or run <code>npm run seed:admin -w backend</code> on the
            server.
          </p>
          <Link to="/" className="btn-primary inline-flex mt-4">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white/80 backdrop-blur border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Logo size="md" />
            <span className="pill bg-gradient-to-r from-brand-600 to-cyan-500 text-white">
              <Shield className="w-3 h-3" /> Admin Console
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="btn-secondary">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <span className="hidden sm:inline text-ink-400 px-2">{user.email}</span>
            <button
              className="btn-ghost"
              onClick={() => {
                logout();
                navigate('/login');
              }}
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-6 sm:py-8 space-y-6">
        {statsQuery.data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Users"
              value={formatNumber(statsQuery.data.users.total)}
              hint={`${statsQuery.data.users.admins} admins`}
              icon={Users}
              variant="brand"
            />
            <StatCard
              label="Domains"
              value={formatNumber(statsQuery.data.domains.total)}
              hint={`${statsQuery.data.domains.verified} verified`}
              icon={Globe}
              variant="cyan"
            />
            <StatCard
              label="Pages"
              value={formatNumber(statsQuery.data.pages.total)}
              hint={`${statsQuery.data.pages.optimized} optimized`}
              icon={Sparkles}
              variant="amber"
            />
            <StatCard
              label="AI events tracked"
              value={formatNumber(
                statsQuery.data.totals.botVisits + statsQuery.data.totals.aiReferrals,
              )}
              hint={`${statsQuery.data.totals.botVisits} bots / ${statsQuery.data.totals.aiReferrals} referrals`}
              icon={Activity}
              variant="green"
            />
          </div>
        )}

        <nav className="border-b border-gray-200 -mx-4 sm:mx-0 px-4 sm:px-0">
          <ul className="flex items-center gap-1 overflow-x-auto scroll-soft">
            <TabLink to="users" icon={Users} label="Users" />
            <TabLink to="domains" icon={Globe} label="Domains" />
            <TabLink to="sales" icon={Receipt} label="Sales" />
            <TabLink to="ipn-logs" icon={Inbox} label="IPN Logs" />
            <TabLink to="mappings" icon={Link2} label="Mappings" />
            <TabLink to="email-templates" icon={Mail} label="Emails" />
            <TabLink to="resellers" icon={Building2} label="Resellers" />
            <TabLink to="subdomains" icon={Globe} label="Subdomains" />
            <TabLink to="activity" icon={History} label="Activity" />
          </ul>
        </nav>

        <Routes>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="domains" element={<DomainsPage />} />
          <Route path="sales" element={<SalesPage />} />
          <Route path="ipn-logs" element={<RawIpnLogsPage />} />
          <Route path="mappings" element={<MappingsPage />} />
          <Route path="email-templates" element={<EmailTemplatesPage />} />
          <Route path="resellers" element={<ResellersPage />} />
          <Route path="subdomains" element={<SubdomainsPage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="*" element={<Navigate to="users" replace />} />
        </Routes>
      </main>

      <AppFooter />
    </div>
  );
}

function TabLink({ to, icon: Icon, label }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          clsx(
            'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
            isActive
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-ink-500 hover:text-ink-900 hover:border-gray-200',
          )
        }
      >
        <Icon className="w-4 h-4" />
        {label}
      </NavLink>
    </li>
  );
}
